import React, { useEffect, useState } from "react";
import { displayErrorToast } from "@/utils/toast";
import { InlineEdit } from "../..";
import { Badge } from "../badge";
import { Button } from "../button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { TitleProps } from "./types";

export const Title: React.FC<TitleProps> = ({
  title,
  onTitleEdit,
  badgeText,
  badgeVariant,
}) => {
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [pendingTitle, setPendingTitle] = useState(title || null);

  useEffect(() => {
    if (pendingTitle === null && title) {
      setPendingTitle(title);
    }
  }, [pendingTitle, title]);

  const handleEdit = (newTitle: string) => {
    if (!newTitle) {
      displayErrorToast("Title must not be empty");
      return;
    }

    setPendingTitle(newTitle);
    setShowWarningDialog(true);
  };

  const handleConfirm = () => {
    onTitleEdit({
      newTitle: pendingTitle!,
      onSuccess: (newTitle) => {
        setPendingTitle(newTitle || title);
      },
      onError: () => {
        setPendingTitle(title);
      },
    });
    setShowWarningDialog(false);
  };

  const handleCancel = () => {
    setPendingTitle(title);
    setShowWarningDialog(false);
  };

  return (
    <>
      <InlineEdit.Root
        initialValue={pendingTitle || ""}
        onEdit={handleEdit}
        className="p-0"
      >
        <InlineEdit.Trigger>
          {!pendingTitle ? (
            <div className="h-8 w-64 animate-pulse rounded bg-gray-50"></div>
          ) : (
            <h1
              className={`cursor-text ${
                pendingTitle.length > 30 ? "text-2xl" : "text-3xl"
              } whitespace-pre-wrap break-words font-semibold ${
                pendingTitle !== title ? "text-blue-600" : ""
              }`}
            >
              {pendingTitle}
              {badgeText && (
                <Badge
                  className="relative -top-[4.5px] ml-2 inline-flex"
                  variant={badgeVariant}
                >
                  {badgeText}
                </Badge>
              )}
            </h1>
          )}
        </InlineEdit.Trigger>
        <InlineEdit.Input className="whitespace-pre-wrap py-1 text-base" />
        <InlineEdit.Rename>Rename</InlineEdit.Rename>
        <InlineEdit.Cancel>Cancel</InlineEdit.Cancel>
      </InlineEdit.Root>

      <Dialog open={showWarningDialog} onOpenChange={handleCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Warning</DialogTitle>
            <DialogDescription>
              Changing the title from "{title}" to "{pendingTitle}" may affect
              existing relationships. Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
